import { useEffect } from 'react'
import siteMeta from '@site-meta'

interface SEOOptions {
  /** Page title — what appears in the browser tab + search results. */
  title: string
  /** Meta description — shown under the title in search results.
   *  Aim for 150-160 chars; longer gets truncated. */
  description?: string
  /** Canonical path (e.g. '/pricing'). The domain is appended
   *  automatically. Defaults to the current pathname. Use this to
   *  disambiguate pages that share content or have multiple valid URLs. */
  canonicalPath?: string
  /** Override whether search engines should index this page. Most
   *  content pages leave this true; auth-gated pages like /dashboard
   *  or /settings pass false. */
  index?: boolean
  /** Structured data for this page, as one schema.org object or several.
   *  Emitted as <script type="application/ld+json"> and torn down when the
   *  page unmounts, so one page's schema never leaks onto the next. */
  jsonLd?: object | object[]
}

const SITE_ORIGIN = 'https://fittohire.com'
const DEFAULT_TITLE_SUFFIX = ' — FitToHire'

function upsertMeta(name: string, value: string, attr: 'name' | 'property' = 'name'): void {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', value)
}

function upsertCanonical(href: string): void {
  let el = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/** Marks the blocks this hook owns, so a page's schema can be torn down on
 *  unmount without touching any static JSON-LD that ships in index.html. */
const LD_OWNED = 'data-seo-jsonld'

function upsertJsonLd(blocks: object[]): void {
  document.head.querySelectorAll(`script[${LD_OWNED}]`).forEach((n) => n.remove())
  for (const block of blocks) {
    const el = document.createElement('script')
    el.type = 'application/ld+json'
    el.setAttribute(LD_OWNED, '')
    el.textContent = JSON.stringify(block)
    document.head.appendChild(el)
  }
}

/**
 * Per-page SEO. Call once from a page component to set its title,
 * description, canonical URL, and OG/Twitter tags. The hook runs on
 * mount + whenever the inputs change, and leaves the DOM in a consistent
 * state without blowing away base tags from index.html.
 *
 * This is a lightweight alternative to react-helmet-async — zero new
 * dependency, plain DOM mutation, which the React SPA's user is fine
 * with because Google's crawler executes JS and reads the final DOM.
 *
 * Example:
 *   useSEO({ title: 'Pricing', description: '...', canonicalPath: '/pricing' })
 */
export function useSEO(options: SEOOptions): void {
  const { title, description, canonicalPath, index = true, jsonLd } = options

  useEffect(() => {
    // Title — include brand suffix unless the caller already added it.
    const fullTitle = title.includes('FitToHire') ? title : `${title}${DEFAULT_TITLE_SUFFIX}`
    document.title = fullTitle

    // Canonical — defaults to current pathname. Strips query/fragment
    // because those rarely belong in a canonical URL (Google treats
    // different params as different pages otherwise).
    const path = canonicalPath ?? window.location.pathname
    const canonicalUrl = `${SITE_ORIGIN}${path}`
    upsertCanonical(canonicalUrl)

    // robots directive — auth-gated pages pass index=false so Google
    // doesn't waste crawl budget on private pages (also prevents
    // 'indexed though blocked by noindex' warnings in Search Console).
    upsertMeta('robots', index ? 'index, follow' : 'noindex, follow')

    // Description — goes under the title in search results.
    if (description) {
      upsertMeta('description', description)
    }

    // Open Graph + Twitter — consistent card rendering across social
    // platforms. og:url matches the canonical.
    upsertMeta('og:title', fullTitle, 'property')
    upsertMeta('og:url', canonicalUrl, 'property')
    if (description) {
      upsertMeta('og:description', description, 'property')
      upsertMeta('twitter:description', description)
    }
    upsertMeta('twitter:title', fullTitle)

    // Structured data. Keyed off the SERIALISED value below rather than the
    // object, because a literal passed at the call site is a new object on
    // every render and would re-fire this effect forever.
    if (jsonLd) upsertJsonLd(Array.isArray(jsonLd) ? jsonLd : [jsonLd])
    return () => {
      if (jsonLd) {
        document.head.querySelectorAll(`script[${LD_OWNED}]`).forEach((n) => n.remove())
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonicalPath, index, JSON.stringify(jsonLd ?? null)])
}


interface SiteMeta {
  origin: string
  pages: Record<string, { title: string; description: string }>
}

/**
 * SEO for a marketing route, read from the file the server reads.
 *
 * The FastAPI layer writes title/description/canonical into the served HTML
 * so a crawler that does not run JS gets them on the first byte. That does
 * nothing for client-side navigation, where React swaps the page without
 * fetching a document — so the tags have to be set again here.
 *
 * Two delivery mechanisms are unavoidable. Two COPIES of the strings are not,
 * and were the actual risk: the head would then depend on how you arrived at
 * the page. Both sides import config/site_meta.json.
 *
 * Falls through to nothing if the path is absent, so an unlisted route keeps
 * whatever the shell gave it rather than rendering "undefined" in a tab.
 */
export function useRouteSEO(path: string, extra?: Omit<SEOOptions, 'title' | 'description' | 'canonicalPath'>): void {
  const page = (siteMeta as SiteMeta).pages?.[path]
  useSEO({
    title: page?.title ?? document.title,
    description: page?.description,
    canonicalPath: path,
    ...extra,
  })
}
